import { HttpService, createHttpService } from '@/services/HttpService';
import { ConsumerProfile } from '@/app/domain/ConsumerProfile';
import { getCountry } from '@/app/library/country-state-picker';
import { getValue } from '@/app/library/getValue';
import { ServerUser, ServerContact } from './ContactService';
import { EmailNotificationsSettings } from '../domain/EmailNotificationsSettings';
import { createUrlSearchParams } from '../library/createUrlSearchParams';
import { SettingsValueResponse } from './CompanyService';

export type ConsumerProfileInformation = {
  firstName?: string;
  lastName?: string;
  login?: string;
  email?: string;
  phone?: string;
  country?: string;
  state?: string;
  city?: string;
  zipCode?: string;
  address?: string;
};

type PutContactResponse = {};
type PutEmailNotificationsSettingsResponse = {};
type PutEmailNotificationsSettingsData = {
  data: EmailNotificationsSettings,
  domain_object_id: string
};

export type EmailNotificationsSettingsResponse = SettingsValueResponse<EmailNotificationsSettings>;

export type ResetPasswordPayload = {
  login: string,
  email: string,
  user_id?: string
};
export class ConsumerProfileService {
  private contactId: string = '';

  private userId: string = '';

  constructor(
    private httpService: HttpService
  ) {

  }

  public async getConsumerProfile(): Promise<ConsumerProfile> {
    try {
      const userResponse: ServerUser = await
        this.httpService.get<void, ServerUser>('/api/2/users/me');
      this.contactId = userResponse.contact.id;
      this.userId = userResponse.id;
      return {
        ...this.convertServerContactInformationToConsumerInformation(
          this.getServerContactInformation(userResponse), userResponse.login
        )
      };
    } catch (error) {
      console.error(error);
      return {} as ConsumerProfile;
    }
  }

  private getServerContactInformation(response: ServerUser): ServerContact {
    return response.contact;
  }

  private convertServerContactInformationToConsumerInformation(
    serverContactInformation: ServerContact,
    login: string
  ): ConsumerProfileInformation {
    return {
      firstName: serverContactInformation.firstname || undefined,
      lastName: serverContactInformation.lastname || undefined,
      login: login || undefined,
      email: serverContactInformation.email || undefined,
      phone: serverContactInformation.phone || undefined,
      country: serverContactInformation.country ? getCountry(serverContactInformation.country)?.code : undefined,
      state: serverContactInformation.state || undefined,
      city: serverContactInformation.city || undefined,
      zipCode: serverContactInformation.zipcode || undefined,
      address: serverContactInformation.address1 || undefined
    };
  }

  public async editConsumerProfile(payload: ConsumerProfileInformation): Promise<void> {
    try {
        await this.httpService.put<Partial<ServerContact>, PutContactResponse>(
          `/api/2/contacts/${this.contactId}`,
          this.convertConsumerProfileToServerContactInformation(payload)
        );
    } catch (error) {

    }
    return Promise.resolve();
  }

  private convertConsumerProfileToServerContactInformation(
    contactInformation: ConsumerProfileInformation
  ): Partial<ServerContact> {
    return {
      id: this.contactId,
      firstname: contactInformation.firstName,
      lastname: contactInformation.lastName,
      email: contactInformation.email,
      phone: contactInformation.phone
      ? getValue(contactInformation.phone, { prefix: '+' })
      : contactInformation.phone,
      country: contactInformation.country?.toUpperCase(),
      state: contactInformation.state,
      city: contactInformation.city,
      zipcode: contactInformation.zipCode,
      address1: contactInformation.address
    };
  }

  public async getEmailNotificationsSettings(): Promise<EmailNotificationsSettings> {
    try {
      const emailNotificationsSettingsReaposnse:EmailNotificationsSettingsResponse = await this.httpService
        .get<void, EmailNotificationsSettingsResponse>(
          '/api/settings/v1/USER/namespaces/com.acronis.marketing/settings/subscriptions/values',
          {
            params: createUrlSearchParams({
              domain_object_id: this.userId
            })
          }
        );
      return this.getServerEmailNotificationsInformation(emailNotificationsSettingsReaposnse);
    } catch (error) {
      console.error(error);
      return {} as EmailNotificationsSettings;
    }
  }

  public async editEmailNotificationsSettings(payload: EmailNotificationsSettings): Promise<void> {
    try {
        await this.httpService.put<PutEmailNotificationsSettingsData, PutEmailNotificationsSettingsResponse>(
          '/api/settings/v1/USER/namespaces/com.acronis.marketing/settings/subscriptions/values',
          {
            data: payload,
            domain_object_id: this.userId
          }
        );
    } catch (error) {
      console.error(error);
    }
    return Promise.resolve();
  }

  private getServerEmailNotificationsInformation(response: EmailNotificationsSettingsResponse):
    EmailNotificationsSettings {
    return response.items[0].data;
  }

  public async resetPassword(payload: ResetPasswordPayload): Promise<void> {
    try {
      await this.httpService.post<ResetPasswordPayload, void>(
        '/api/1/actions/mail/reset',
        {
          email: payload.email,
          login: payload.login,
          user_id: this.userId
        }
      );
    } catch (error) {

    }
    return Promise.resolve();
  }
}

export function createConsumerProfileService(selfTenantUuid: string): ConsumerProfileService {
  const httpService: HttpService = createHttpService(selfTenantUuid);

  return new ConsumerProfileService(httpService);
}
